import { useState } from "react";
import { clsx } from "clsx";
import { Pin, PinOff, Plus, Search, Users, X } from "lucide-react";
import { formatDate } from "@/utils/formatDate";
import type { ChatRoomBrief, ChatUserBrief } from "../types";
import { Avatar } from "./Avatar";
import { GlobalSearchPanel } from "./GlobalSearchPanel";

interface ChatRoomListProps {
  rooms: ChatRoomBrief[];
  selectedId: string | null;
  onSelect: (roomId: string) => void;
  onOpenMessage?: (roomId: string, messageId: string) => void;
  onNew?: () => void;
  /** Pin/unpin from the row itself (hover action); absent in the archive view. */
  onTogglePin?: (room: ChatRoomBrief) => void;
  isLoading: boolean;
  currentUserId: string | undefined;
  variant: "active" | "archive";
  fullWidthOnMobile?: boolean;
}

function previewLine(
  room: ChatRoomBrief,
  currentUserId: string | undefined,
): string {
  if (!room.last_message) return "Brak wiadomości";
  if (room.last_message.is_deleted) return "[wiadomość usunięta]";
  const body = room.last_message.body || "";
  // System messages (sender=null) are rendered verbatim — they're already
  // self-describing ("~X added Y to the group" etc.).
  if (!room.last_message.sender) return body;
  const mine = room.last_message.sender.id === currentUserId;
  if (mine) return body ? `Ty: ${body}` : "Ty: [załącznik]";
  const sender = room.last_message.sender.full_name?.split(" ")[0] || "";
  return sender ? `${sender}: ${body}` : body;
}

const WEEKDAYS_SHORT_PL = ["nd", "pn", "wt", "śr", "cz", "pt", "sb"];

/** Compact activity stamp for the list: clock today, weekday this week, date
 *  beyond that — the same shorthand every messenger uses. */
function shortStamp(iso: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
    }
    const days = Math.round(
      (new Date(now.toDateString()).getTime() - new Date(d.toDateString()).getTime()) /
        86_400_000,
    );
    if (days === 1) return "wczoraj";
    if (days < 7) return WEEKDAYS_SHORT_PL[d.getDay()];
    return formatDate(d, "");
  } catch {
    return "";
  }
}

/** For DIRECT rooms, find the other member (skip me). */
function otherMember(
  members: ChatUserBrief[],
  currentUserId: string | undefined,
): ChatUserBrief | null {
  return members.find((m) => m.id !== currentUserId) ?? members[0] ?? null;
}

export function ChatRoomList({
  rooms,
  selectedId,
  onSelect,
  onOpenMessage,
  onNew,
  onTogglePin,
  isLoading,
  currentUserId,
  variant,
  fullWidthOnMobile,
}: ChatRoomListProps) {
  const [query, setQuery] = useState("");
  const searching = variant === "active" && query.trim().length > 0;
  return (
    <div
      className={clsx(
        "flex flex-shrink-0 flex-col border-r border-gray-200 bg-white",
        fullWidthOnMobile ? "w-full sm:w-72" : "w-72",
      )}
    >
      <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2.5">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
          {variant === "archive" ? "Zarchiwizowane" : "Rozmowy"}
        </h2>
        {onNew && (
          <button
            type="button"
            onClick={onNew}
            title="Nowa rozmowa"
            className="flex items-center gap-1 border border-gray-200 px-1.5 py-1 text-[11px] font-medium text-gray-600 transition-colors hover:border-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <Plus size={14} /> Nowa
          </button>
        )}
      </div>
      {variant === "active" && (
        <div className="px-3 py-2 border-b border-gray-200">
          <div className="relative">
            <Search
              size={13}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Szukaj osób lub wiadomości…"
              className="w-full border border-gray-200 bg-gray-50 py-1.5 pl-7 pr-6 text-xs text-gray-900 placeholder:text-gray-400 focus:border-red-500 focus:bg-white focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                title="Wyczyść"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>
      )}
      {searching ? (
        <GlobalSearchPanel
          query={query}
          rooms={rooms}
          currentUserId={currentUserId}
          onSelectRoom={(roomId) => {
            setQuery("");
            onSelect(roomId);
          }}
          onOpenMessage={(roomId, messageId) => {
            setQuery("");
            if (onOpenMessage) onOpenMessage(roomId, messageId);
            else onSelect(roomId);
          }}
        />
      ) : (
      <div className="flex-1 divide-y divide-gray-100 overflow-y-auto">
        {isLoading && (
          <div className="p-4 text-xs text-gray-400">Ładowanie…</div>
        )}
        {!isLoading && rooms.length === 0 && (
          <div className="p-4 text-xs text-gray-400">
            {variant === "archive"
              ? "Brak zarchiwizowanych rozmów."
              : "Brak rozmów."}
          </div>
        )}
        {rooms.map((room) => {
          const other = otherMember(room.members ?? [], currentUserId);
          const title =
            room.kind === "direct"
              ? other?.full_name || other?.username || "Rozmowa"
              : room.name || "Pokój grupowy";
          const groupPic = room.picture?.url ?? null;
          const unread = room.unread_count > 0;
          return (
            // A div, not a <button>: the row hosts its own pin button and
            // nested buttons are invalid HTML. Keyboard activation is wired by
            // hand below.
            <div
              key={room.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(room.id)}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(room.id);
                }
              }}
              className={clsx(
                "group flex w-full cursor-pointer gap-2.5 border-l-[3px] px-3 py-2.5 text-left transition-colors focus:outline-none focus-visible:bg-gray-50",
                selectedId === room.id
                  ? "border-red-600 bg-red-50"
                  : "border-transparent hover:bg-gray-50",
              )}
            >
              {room.kind === "direct" ? (
                <Avatar user={other} size={36} />
              ) : groupPic ? (
                <img
                  src={groupPic}
                  alt={title}
                  className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center bg-gray-100 text-gray-500">
                  <Users size={16} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span
                    className={clsx(
                      "truncate text-[13px]",
                      unread ? "font-bold text-gray-900" : "font-semibold text-gray-800",
                    )}
                  >
                    {title}
                  </span>
                  <span className="flex flex-shrink-0 items-center gap-1 font-mono text-[10px] text-gray-400">
                    {onTogglePin ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onTogglePin(room);
                        }}
                        title={
                          room.is_pinned ? "Odepnij rozmowę" : "Przypnij na górze"
                        }
                        aria-label={
                          room.is_pinned ? "Odepnij rozmowę" : "Przypnij na górze"
                        }
                        className={clsx(
                          "flex items-center text-gray-400 transition-opacity hover:text-red-600 focus:outline-none focus-visible:opacity-100",
                          // Pinned rows always show their pin; others reveal
                          // the action on hover only.
                          room.is_pinned
                            ? "opacity-100"
                            : "opacity-0 group-hover:opacity-100",
                        )}
                      >
                        {room.is_pinned ? (
                          <>
                            <Pin size={11} className="group-hover:hidden" />
                            <PinOff size={11} className="hidden group-hover:block" />
                          </>
                        ) : (
                          <Pin size={11} />
                        )}
                      </button>
                    ) : (
                      room.is_pinned && (
                        <Pin size={11} aria-label="Przypięta" />
                      )
                    )}
                    {shortStamp(room.last_message_at)}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center justify-between gap-2">
                  <span
                    className={clsx(
                      "truncate text-xs",
                      unread ? "text-gray-800" : "text-gray-500",
                    )}
                  >
                    {previewLine(room, currentUserId)}
                  </span>
                  {unread && (
                    <span className="min-w-[18px] flex-shrink-0 bg-red-600 px-1.5 py-0.5 text-center font-mono text-[10px] font-semibold leading-none text-white">
                      {room.unread_count}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
